import { tokens } from '@actual-app/components/tokens';

import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';

/**
 * Tokens visuais do FinApp.
 *
 * ⭐⭐ FONTE DA VERDADE: o **MULTICAP** (planilha.multicap.com.br), o app que o
 * dono mandou copiar. Estes valores foram MEDIDOS nas telas dele — Orçamento e
 * Patrimônio — em 14/09/2026, não derivados de descrição.
 *
 * ⚠️ Eles corrigem a leitura anterior ("direção Extrato"), que tinha três
 * invenções que não existem no MULTICAP: valores em fonte SERIFADA, cartão sem
 * borda, e fundo cinza-esverdeado. O MULTICAP usa sans em tudo, cartão branco
 * com borda finíssima e fundo quase branco.
 *
 * Estes tokens valem nas telas do FinApp (`components/finapp/`). As telas
 * nativas do Actual seguem no estilo original delas.
 */

export const cor = {
  /** Fundo da área de conteúdo — quase branco, não cinza. */
  fundo: '#fafafa',
  cartao: '#ffffff',
  /** Borda finíssima do cartão. O MULTICAP usa borda; ela é sutil, não some. */
  borda: '#ececef',
  tinta: '#111827',
  tintaFraca: '#6b7280',
  /** Verde da marca — o mesmo do card protagonista e do menu. */
  acento: '#15654a',

  /**
   * ⭐ Cor por NATUREZA do dinheiro, medida no MULTICAP: cada valor diz o que
   * é pela cor, antes de o rótulo ser lido. Receita verde, despesa vermelha,
   * investimento azul, saldo neutro.
   */
  entrada: '#15803d',
  saida: '#dc2626',
  investimento: '#2563eb',
  neutro: '#111827',

  /** Fundo suave dos quadradinhos de ícone e dos chips. */
  entradaFraca: '#e8f5ee',
  saidaFraca: '#fdeaea',
  investimentoFraca: '#e8effd',
  neutroFraco: '#f2f3f5',
} as const;

/** Apelidos mantidos porque as telas já os usam. */
export const negativoLegado = cor.saida;

export const espaco = {
  cartao: 28,
  entreBlocos: 20,
  /** Raio grande — medido em ~16px nos cards deles. */
  raio: 16,
  raioPequeno: 12,
  alturaLinha: 56,
} as const;

export const fonte = {
  /**
   * ⚠️ Sans em TUDO, inclusive nos valores. A serifa nos números era invenção
   * da leitura anterior; o MULTICAP não tem nada serifado.
   */
  ui: "'Public Sans Variable', 'Public Sans', system-ui, sans-serif",
} as const;

export const texto = {
  /** Título da página ("Patrimônio", "Orçamento") — grande e pesado. */
  titulo: {
    fontFamily: fonte.ui,
    fontWeight: 700,
    fontSize: 32,
    letterSpacing: '-.02em',
    color: cor.tinta,
  },
  /** Saudação pequena acima do título ("Olá, Christian"). */
  sobretitulo: {
    fontFamily: fonte.ui,
    fontWeight: 400,
    fontSize: 15,
    color: cor.tintaFraca,
  },
  /** O número-herói, dentro do card de destaque. */
  valorGrande: {
    fontFamily: fonte.ui,
    fontWeight: 700,
    fontSize: 40,
    letterSpacing: '-.02em',
    fontVariantNumeric: 'tabular-nums' as const,
    color: cor.tinta,
  },
  /** O valor de um card de KPI. */
  valorCartao: {
    fontFamily: fonte.ui,
    fontWeight: 700,
    fontSize: 30,
    letterSpacing: '-.02em',
    fontVariantNumeric: 'tabular-nums' as const,
    color: cor.tinta,
  },
  valorMedio: {
    fontFamily: fonte.ui,
    fontWeight: 600,
    fontSize: 18,
    fontVariantNumeric: 'tabular-nums' as const,
    color: cor.tinta,
  },
  /** Rótulo MAIÚSCULO de card ("PATRIMÔNIO TOTAL", "LIQUIDEZ"). */
  rotuloCartao: {
    fontFamily: fonte.ui,
    fontWeight: 600,
    fontSize: 14,
    letterSpacing: '.08em',
    textTransform: 'uppercase' as const,
    color: cor.tintaFraca,
  },
  /** Cabeçalho de coluna de tabela. */
  rotuloTabela: {
    fontFamily: fonte.ui,
    fontWeight: 500,
    fontSize: 13,
    color: cor.tintaFraca,
  },
  /** Título de um bloco ("Composição do patrimônio"). */
  tituloBloco: {
    fontFamily: fonte.ui,
    fontWeight: 700,
    fontSize: 18,
    color: cor.tinta,
  },
  corpo: {
    fontFamily: fonte.ui,
    fontWeight: 400,
    fontSize: 15,
    color: cor.tinta,
  },
} as const;

/**
 * Paleta dos gráficos. Medida no donut deles: verde da marca como principal e
 * cores francamente distintas nas fatias menores — não uma escala do mesmo
 * verde, que embaralharia as fatias pequenas.
 */
export const coresCategoricas = [
  '#15803d',
  '#2563eb',
  '#7c3aed',
  '#db2777',
  '#ea580c',
  '#0891b2',
] as const;

/**
 * Cartão padrão: branco, **borda finíssima**, raio grande, sem sombra.
 *
 * ⚠️ `flexShrink: 0` é obrigatório: `View` é flex e, num container de altura
 * limitada, o padrão `flex-shrink: 1` espreme os blocos até as linhas caírem
 * umas sobre as outras.
 */
export const cartao = {
  backgroundColor: cor.cartao,
  border: `1px solid ${cor.borda}`,
  borderRadius: espaco.raio,
  padding: espaco.cartao,
  boxShadow: 'none',
  flexShrink: 0,
} as const;

/**
 * ⭐ O cartão do número protagonista: **verde escuro com texto branco**. É
 * assim que o MULTICAP destaca o número principal da tela — por inversão de
 * cor, não só por tamanho. Um por tela, no máximo.
 */
export const cartaoDestaque = {
  backgroundColor: cor.acento,
  border: `1px solid ${cor.acento}`,
  borderRadius: espaco.raio,
  padding: espaco.cartao,
  boxShadow: 'none',
  flexShrink: 0,
} as const;

/**
 * O mesmo cartão de destaque, em vermelho escuro.
 *
 * ⚠️ Existe porque o número protagonista do FinApp pode ser NEGATIVO ("livre
 * pra decidir" no vermelho), o que não acontece no MULTICAP — lá o destaque é
 * o patrimônio total. Com o valor em branco sobre verde, um mês estourado
 * ficaria com a mesma cara de um mês tranquilo. O card inteiro troca de cor
 * em vez de só o número: sobre fundo escuro, um número vermelho não teria
 * contraste suficiente para ser lido como alerta.
 */
export const cartaoDestaqueAlerta = {
  ...cartaoDestaque,
  backgroundColor: '#991b1b',
  border: '1px solid #991b1b',
} as const;

/**
 * Container externo de toda tela nossa.
 *
 * ⚠️ O `overflowY` é obrigatório: no desktop o `Page` do Actual monta o
 * conteúdo com `flex: 1` e **sem** overflow, então a área da página nunca rola
 * sozinha e os blocos se sobrepõem.
 *
 * ⚠️⚠️ Validação com dado real, 22/09/2026 — em 390px, depois de rolar até o
 * fim da tela Mês, a barra de navegação FIXA do celular (`MobileNavTabs`)
 * cobria os rótulos de mês (abr...set) do gráfico "O que entrou, mês a mês"
 * em 50px, sem forma de rolar mais pra revelar. Corrigido aqui, no token
 * COMPARTILHADO pelas quatro telas do FinApp (Mês, Médias, Compromissos,
 * Patrimônio via `Inicio.tsx`/`Medias.tsx`/`Compromissos.tsx`/
 * `Patrimonio.tsx`) — não só na tela Mês — porque qualquer uma delas pode
 * crescer o suficiente pra bater no mesmo problema, e um respiro que só
 * existisse na tela Mês teria que ser copiado nas outras três na primeira
 * vez que alguém rolasse até o fim de qualquer uma. `MOBILE_NAV_HEIGHT` é a
 * altura EXATA da barra (import do próprio componente do upstream — nunca
 * duplicar o número aqui). Só em largura estreita (`@media`, mesmo
 * breakpoint de `isNarrowWidth`): no desktop não existe barra fixa embaixo,
 * e um respiro extra ali seria espaço em branco sem função.
 */
export const tela = {
  flex: 1,
  overflowY: 'auto',
  backgroundColor: cor.fundo,
  gap: espaco.entreBlocos,
  padding: espaco.entreBlocos,
  [`@media (max-width: ${parseInt(tokens.breakpoint_small, 10) - 1}px)`]: {
    paddingBottom: MOBILE_NAV_HEIGHT,
  },
} as const;
